import { Controller } from 'stimulus'
import Dropdown from 'bootstrap/js/src/dropdown'

import { parse, stringify } from 'utils/qs'
import { visitQuery } from 'utils/uri'
import { attr } from 'utils/dom'
import debounce from 'utils/debounce_promise'
import {
  dissoc,
  isEmpty,
  mergeRight,
  pick,
  reduce,
  objDiff
} from 'utils'
const formatPrice = n => (n == null || Number.isNaN(n)) ? '' : new Intl.NumberFormat('de').format(n)

export default class extends Controller {
  static targets = ['widget', 'sortForm']
  static values = {
    anchor: String,
    defaultSort: String,
    filterEventName: String,
    sortEventName: String,
    initialFacets: String, // pre-computed facets JSON from server
    context: String // filter context (condition, ids, categories) from backend
  }

  connect () {
    this.currentQueryParams = parse(window.location.search)
    this.facetsData = {}
    this.showAllState = {}
    this.priceStats = null
    this.isDraggingPrice = false
    this.loadFacetsSoon = debounce(() => this.loadFacets(), 300) // the slider fires on every step
    this.useInitialFacets()
  }

  disconnect () {
    this.loadFacetsSoon.cancel()
    this.facetsRequest?.abort()
  }

  // The server embeds facets computed from the same filters as the page's hits, so the
  // first render never needs a request. /facets is only for changes made on the page.
  useInitialFacets () {
    try {
      this.renderFacets(JSON.parse(this.initialFacetsValue))
      this.facetsLoaded = true
    } catch (error) {
      console.error('Error parsing initial facets:', error)
      this.loadFacets()
    }
  }

  startSearch () {
    if (!this.facetsLoaded) this.loadFacets()
  }

  // Only what narrows the facets: the page context from the server, the search term,
  // and the values of this controller's widgets. Paging, sorting and tracking params stay out.
  get facetParams () {
    const context = this.contextValue ? JSON.parse(this.contextValue) : {}
    const names = ['query']
    this.eachWidget(({ queryParameterName }) => names.push(queryParameterName))
    const selected = Object.fromEntries(
      names.map(name => [name, this.currentQueryParams[name]])
        .filter(([, value]) => Array.isArray(value) ? value.length : value)
    )
    return { ...selected, ...context }
  }

  async loadFacets () {
    const [, pathLanguage = 'de', locale = 'de'] = window.location.pathname.split('/')
    this.facetsRequest?.abort() // a newer selection makes the pending answer stale
    this.facetsRequest = new AbortController()
    try {
      const response = await fetch(`/${pathLanguage}/${locale}/facets`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: stringify(this.facetParams),
        signal: this.facetsRequest.signal
      })
      this.renderFacets(await response.json())
      this.facetsLoaded = true
    } catch (error) {
      if (error.name !== 'AbortError') console.error('Error loading facets:', error)
    }
  }

  renderFacets (data) {
    if (!data?.aggs) return console.error('Invalid facets data:', data)

    const stats = data.aggs.retail_price_i_stats
    if (stats) this.priceStats = stats
    this.eachWidget(({ attribute, widgetName }, el) => {
      const facetData = data.aggs[attribute]
      if (widgetName === 'rangeSlider' && stats) {
        if (!this.isDraggingPrice) {
          this.renderRangeSlider(el, attribute, stats)
        }
      } else if (widgetName === 'customRangeInput' && stats) this.renderCustomRangeInput(el, attribute, stats)
      else if (widgetName === 'customRefinementList') {
        this.facetsData[attribute] = facetData?.buckets || []
        this.renderWidgetItems(el, attribute)
      }
    })
  }

  renderRangeSlider (container, attribute, stats) {
    const { min, max } = stats
    let [currentMin = min, currentMax = max] = this.currentQueryParams.price?.split(':').map(v => parseInt(v)) || []
    currentMin = Math.max(min, Math.min(max, currentMin))
    currentMax = Math.max(min, Math.min(max, currentMax))

    container.innerHTML = `
      <div class="dual-range-slider">
        <div class="slider-track"></div>
        <div class="slider-progress"></div>
        <input type="range" class="form-range range-min" min="${min}" max="${max}" value="${currentMin}" step="1"
               data-action="input->${this.identifier}#handleRangeChange mousedown->${this.identifier}#handleRangeStart mouseup->${this.identifier}#handleRangeEnd touchstart->${this.identifier}#handleRangeStart touchend->${this.identifier}#handleRangeEnd" data-position="min">
        <input type="range" class="form-range range-max" min="${min}" max="${max}" value="${currentMax}" step="1"
               data-action="input->${this.identifier}#handleRangeChange mousedown->${this.identifier}#handleRangeStart mouseup->${this.identifier}#handleRangeEnd touchstart->${this.identifier}#handleRangeStart touchend->${this.identifier}#handleRangeEnd" data-position="max">
      </div>
    `
    this.updateSliderProgress(container.querySelector('.dual-range-slider'), min, max, currentMin, currentMax)
  }

  renderCustomRangeInput (container, attribute, stats) {
    const { min, max } = stats
    let [currentMin = min, currentMax = max] = this.currentQueryParams.price?.split(':').map(v => parseInt(v)) || []

    currentMin = Math.max(min, Math.min(max, currentMin))
    currentMax = Math.max(min, Math.min(max, currentMax))

    container.innerHTML = `
      <div class="input-group d-inline-flex" style="max-width: 6.5rem;">
        <input type="number" class="form-control" placeholder="${formatPrice(min)}" value="${formatPrice(currentMin)}"
               data-action="change->${this.identifier}#handleRangeChange" data-position="min">
        <span class="input-group-text">€</span>
      </div>
      <span class="mx-2">bis</span>
      <div class="input-group d-inline-flex" style="max-width: 6.5rem;">
        <input type="number" class="form-control" placeholder="${formatPrice(max)}" value="${formatPrice(currentMax)}"
               data-action="change->${this.identifier}#handleRangeChange" data-position="max">
        <span class="input-group-text">€</span>
      </div>
    `
  }

  handleRangeStart (e) {
    this.isDraggingPrice = true
  }

  handleRangeEnd (e) {
    this.isDraggingPrice = false
  }

  handleRangeChange (e) {
    const container = e.target.closest('[data-items-filters-target="widget"]')
    const minInput = container.querySelector('[data-position="min"]')
    const maxInput = container.querySelector('[data-position="max"]')
    const sliderContainer = container.querySelector('.dual-range-slider')

    let minValue = parseInt(minInput.value)
    let maxValue = parseInt(maxInput.value)
    if (e.target.dataset.position === 'min' && minValue > maxValue) {
      minValue = maxValue
      minInput.value = formatPrice(minValue)
    } else if (e.target.dataset.position === 'max' && maxValue < minValue) {
      maxValue = minValue
      maxInput.value = formatPrice(maxValue)
    }

    this.currentQueryParams.price = `${minValue}:${maxValue}`

    if (sliderContainer && this.priceStats) {
      this.updateSliderProgress(sliderContainer, this.priceStats.min, this.priceStats.max, minValue, maxValue)
    }

    const customRangeContainer = this.element.querySelector('[data-items-filters-widget="customRangeInput"]')
    if (customRangeContainer) {
      const customMinInput = customRangeContainer.querySelector('[data-position="min"]')
      const customMaxInput = customRangeContainer.querySelector('[data-position="max"]')
      if (customMinInput) customMinInput.value = formatPrice(minValue)
      if (customMaxInput) customMaxInput.value = formatPrice(maxValue)
    }
    this.loadFacetsSoon()
  }

  updateSliderProgress (sliderContainer, minLimit, maxLimit, minValue, maxValue) {
    const progressBar = sliderContainer.querySelector('.slider-progress')
    if (!progressBar) return

    const range = maxLimit - minLimit
    if (range === 0) return

    minValue = Math.max(minLimit, Math.min(maxLimit, minValue))
    maxValue = Math.max(minLimit, Math.min(maxLimit, maxValue))

    progressBar.style.left = `${((minValue - minLimit) / range) * 100}%`
    progressBar.style.right = `${((maxLimit - maxValue) / range) * 100}%`
  }

  isRefined (attribute, value) {
    if (value === 'Alle') return false
    const currentValue = this.currentQueryParams[attribute]
    return Array.isArray(currentValue) ? currentValue.includes(value) : currentValue === value
  }

  renderWidgetItems (container, attribute) {
    const buckets = this.facetsData[attribute] || []
    const showAll = this.showAllState[attribute] || false
    const limit = 6
    const currentValues = this.currentQueryParams[attribute]
    const isAlleChecked = !(Array.isArray(currentValues) ? currentValues.length : currentValues)
    const scrollTop = container.querySelector('ul')?.scrollTop || 0

    const urlParams = parse(window.location.search)
    const appliedValues = urlParams[attribute]
    const selectedValues = Array.isArray(appliedValues) ? appliedValues : (appliedValues ? [appliedValues] : [])
    const order = container.dataset.itemsFiltersOrder ? JSON.parse(container.dataset.itemsFiltersOrder) : null

    const sortedBuckets = [...buckets].sort((a, b) => {
      const aSelected = selectedValues.includes(a.key)
      const bSelected = selectedValues.includes(b.key)
      if (aSelected && !bSelected) return -1
      if (!aSelected && bSelected) return 1
      if (order) {
        const aIdx = order.indexOf(a.key)
        const bIdx = order.indexOf(b.key)
        if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx
      }
      return a.key.localeCompare(b.key)
    })

    const isLimitApplicable = sortedBuckets.length > (limit + 1)
    const itemsToShow = (!showAll && isLimitApplicable) ? sortedBuckets.slice(0, limit) : sortedBuckets

    const alleItem = `<li class="form-check">
        <input class="form-check-input" type="checkbox" name="${attribute}" value="Alle"
               id="${attribute}_Alle" ${isAlleChecked ? 'checked' : ''} data-action="change->${this.identifier}#handleFilterChange">
        <label class="form-check-label" for="${attribute}_Alle">Alle</label>
      </li>`

    const listItems = itemsToShow.map(bucket => {
      const id = `${attribute}_${bucket.key.replace(/\s+/g, '_')}`
      const isRefined = this.isRefined(attribute, bucket.key)
      return `
        <li class="form-check">
          <input class="form-check-input" type="checkbox" name="${attribute}" value="${bucket.key}"
                 id="${id}" ${isRefined ? 'checked' : ''} data-action="change->${this.identifier}#handleFilterChange">
          <label class="form-check-label" for="${id}">${bucket.key}</label>
        </li>
      `
    }).join('')

    const buttonHtml = isLimitApplicable
      ? `
      <button type="button" class="btn btn-link btn-sm mt-1 ms-4" data-action="click->${this.identifier}#showMore" data-attribute="${attribute}">
        ${showAll ? 'Weniger' : `(+ ${buckets.length - limit}) Mehr`}
      </button>
    `
      : ''

    container.innerHTML = `<ul class="no-style mb-0">${alleItem}${listItems}</ul>${buttonHtml}`
    container.querySelector('ul')?.scrollTo(0, scrollTop)
  }

  handleFilterChange (e) {
    const { name, value, checked } = e.target
    const container = e.target.closest('[data-items-filters-target="widget"]')

    if (value === 'Alle' && checked) {
      this.currentQueryParams[name] = null
      container.querySelectorAll(`input[name="${name}"]:not([value="Alle"])`).forEach(cb => { cb.checked = false })
    } else {
      const currentValues = Array.isArray(this.currentQueryParams[name])
        ? this.currentQueryParams[name]
        : this.currentQueryParams[name] ? [this.currentQueryParams[name]] : []

      if (checked) {
        const alleCheckbox = container.querySelector(`input[name="${name}"][value="Alle"]`)
        if (alleCheckbox) alleCheckbox.checked = false
        this.currentQueryParams[name] = [...currentValues, value]
      } else {
        const newValues = currentValues.filter(v => v !== value)
        this.currentQueryParams[name] = newValues.length ? newValues : null
        if (!newValues.length) {
          const alleCheckbox = container.querySelector(`input[name="${name}"][value="Alle"]`)
          if (alleCheckbox) alleCheckbox.checked = true
        }
      }
    }
    this.loadFacets()
  }

  showMore (e) {
    e.preventDefault()
    const attribute = e.target.dataset.attribute
    this.showAllState[attribute] = !this.showAllState[attribute]
    this.renderWidgetItems(e.target.closest('[data-items-filters-target="widget"]'), attribute)
  }

  apply (e) {
    e.preventDefault()
    this.sendEvents()

    const { target } = e
    this.maybeHideDropdown(target)

    const newQueryParams = mergeRight(this.otherQueryParams, this.filterQueryParams)
    visitQuery(newQueryParams, this.anchorValue)
  }

  /**
   * If "Apply" was pressed inside dropdowns, we need to programatically hide it,
   * otherwise Turbo would store page with opened dropdown and returning back
   * or clicking "Reset" would show page with dropdown already opened
   *
   * @param {Element} trigger apply button
   */
  maybeHideDropdown (trigger) {
    Dropdown.getInstance(trigger.closest('.dropdown-menu')?.previousElementSibling)?.hide()
  }

  reset (e) {
    e.preventDefault()
    visitQuery(this.otherQueryParams, this.anchorValue)
  }

  sendEvents () {
    const { sort: newSortValue, ...newFilters } = this.filterQueryParams
    const { sort: previousSortValue = this.defaultSortValue, ...previousFilters } =
      pick(this.filterQueryNames, this.currentQueryParams)

    if (this.hasSortFormTarget && newSortValue !== previousSortValue) {
      dataLayer.push({ event: this.sortEventNameValue, value: newSortValue })
    }

    const changes = objDiff(previousFilters, newFilters)
    if (!isEmpty(changes)) {
      dataLayer.push({ event: this.filterEventNameValue, value: newFilters })
    }
  }

  get filterQueryParams () {
    const queryParams = {}
    if (this.hasSortFormTarget) {
      Object.assign(queryParams, Object.fromEntries(new FormData(this.sortFormTarget).entries()))
    }
    if (this.currentQueryParams.price) queryParams.price = this.currentQueryParams.price
    this.eachWidget(({ attribute, queryParameterName }) => {
      if (queryParameterName === 'price') return
      const currentValue = this.currentQueryParams[attribute]
      const value = Array.isArray(currentValue) ? currentValue : (currentValue ? [currentValue] : [])
      if (value.length) queryParams[queryParameterName] = value
    })
    return queryParams
  }

  get otherQueryParams () {
    return reduce((params, name) => dissoc(name, params), this.currentQueryParams, this.filterQueryNames)
  }

  get filterQueryNames () {
    const names = this.hasSortFormTarget ? ['sort'] : []
    this.eachWidget(({ queryParameterName }) => names.push(queryParameterName))
    return names
  }

  eachWidget (callback) {
    this.widgetTargets.forEach((el) => callback(this.getWidgetSettings(el), el))
  }

  getWidgetSettings (el) {
    const widgetName = attr(`data-${this.identifier}-widget`, el) // widget name
    const attribute = attr(`data-${this.identifier}-attribute`, el) // searchable attribute, e.g. manufacturer
    const queryParameterName = attr(`data-${this.identifier}-qparam`, el) || attribute // according query parameter, defaults to attribute

    return { widgetName, attribute, queryParameterName }
  }
}
